import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  type ReactNode,
} from "react";

const DEFAULT_TITLE = "Notice";
const DEFAULT_DURATION_MS = 5000;

type OpenModalOptions = {
  description: string;
  title?: string;
  duration?: number;
};

type ModalContextType = {
  showModal: boolean;
  description: string;
  title: string;
  openModal: (options: OpenModalOptions) => void;
  closeModal: () => void;
};

const ModalContext = createContext<ModalContextType | undefined>(undefined);
ModalContext.displayName = "ModalContext";

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [showModal, setShowModal] = useState(false);
  const [description, setDescription] = useState("");
  const [title, setTitle] = useState("");
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const closeModal = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setShowModal(false);
  }, []);

  const openModal = useCallback(
    ({
      description: desc,
      title: titleOption = DEFAULT_TITLE,
      duration = DEFAULT_DURATION_MS,
    }: OpenModalOptions) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setDescription(desc);
      setTitle(titleOption);
      setShowModal(true);
      timeoutRef.current = setTimeout(() => {
        setShowModal(false);
        timeoutRef.current = null;
      }, duration);
    },
    []
  );

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  const value = useMemo<ModalContextType>(
    () => ({
      showModal,
      description,
      title,
      openModal,
      closeModal,
    }),
    [showModal, description, title, openModal, closeModal]
  );

  return (
    <ModalContext.Provider value={value}>
      {children}
    </ModalContext.Provider>
  );
};

export const useModal = (): ModalContextType => {
  const context = useContext(ModalContext);
  if (!context) throw new Error("useModal must be used within ModalProvider");
  return context;
};
